import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import SimpleLogin from '../components/auth/SimpleLogin';
import ResetPassword from '../components/auth/ResetPassword';
import logo from '../assets/logo.png';
import { AuthContainer, AuthCard, Logo } from './App.styles';
import PageSpinner from '../components/common/PageSpinner';

// Public pages are split out of the main bundle (checkout, catalog, QR code and survey code
// would otherwise download for every visitor, including the login page).
const AttendanceCheckIn = lazy(() => import('../components/attendance/AttendanceCheckIn'));
const ExtensionActionLanding = lazy(() => import('../components/extensions/ExtensionActionLanding'));
const PublicSurveyPlayer = lazy(() => import('../pages/public/surveys/PublicSurveyPlayer'));
const SurveyClassLink = lazy(() => import('../pages/public/surveys/SurveyClassLink'));
const PurchaseConfirmation = lazy(() => import('../components/payment/PurchaseConfirmation'));
const DonationForm = lazy(() => import('../components/payment/DonationForm'));
const DonationConfirmation = lazy(() => import('../components/payment/DonationConfirmation'));
const EnrollmentFlow = lazy(() => import('../components/payment/EnrollmentFlow'));
const CourseCatalog = lazy(() => import('../components/payment/CourseCatalog'));
const GuestPayment = lazy(() => import('../components/payment/GuestPayment'));
const GuestCheckoutComplete = lazy(() => import('../components/payment/GuestCheckoutComplete'));

// Any logged-out-only path that isn't one of the explicit public routes
// above (e.g. a direct link to /instructor/surveys/1/preview, /admin/...,
// /student/...) lands here. Without preserving where they were headed,
// LoginRedirect.jsx falls back to its own default (/dashboard) once they
// log in — so this carries the attempted path+search through as the same
// ?redirect= param the explicit routes above already use (see
// /attendance/checkin's Navigate a few lines up).
export function loginPathFor(pathname, search) {
  // Logging out clears the user while the URL is still the page they were on, so
  // the catch-all fires right away. Don't make that page the post-login
  // destination (AuthContext.logout sets this flag; the login page clears it).
  if (localStorage.getItem('faithinsight_logged_out') === 'true') return '/login';
  return `/login?redirect=${encodeURIComponent(pathname + search)}`;
}

function RedirectToLoginWithReturn() {
  const location = useLocation();
  return <Navigate to={loginPathFor(location.pathname, location.search)} replace />;
}

// A logged-out visitor opening a direct payment URL is sent to the email-first
// enrollment step (which signs existing students in inline) instead of the
// generic login page. The authenticated /enroll/flow and /enroll/products
// routes bounce signed-in users straight back to payment.
function RedirectToEnrollmentFlow({ kind }) {
  const { courseId, courseIds, productIds } = useParams();
  const { search } = useLocation();
  if (kind === 'products') return <Navigate to={`/enroll/products/${productIds}`} replace />;
  return <Navigate to={`/enroll/flow/${courseIds || courseId}${search}`} replace />;
}

function PublicRoutes() {
  return (
    <Suspense fallback={<PageSpinner />}>
    <Routes>
        {/* Public routes that don't need auth card styling */}
        <Route path="/purchase/confirmation" element={<PurchaseConfirmation />} />
        <Route path="/confirmation" element={<PurchaseConfirmation />} />
        <Route path="/catalog" element={<CourseCatalog />} />
        <Route path="/enroll/flow/:courseIds" element={<EnrollmentFlow />} />
        <Route path="/enroll/products/:productIds" element={<EnrollmentFlow />} />
        <Route path="/enroll/guest/complete" element={<GuestCheckoutComplete />} />
        <Route path="/enroll/guest/products/:productIds" element={<GuestPayment />} />
        <Route path="/enroll/guest/:courseIds" element={<GuestPayment />} />
        <Route path="/enroll/multiple/:courseIds" element={<RedirectToEnrollmentFlow kind="courses" />} />
        <Route path="/purchase/products/:productIds" element={<RedirectToEnrollmentFlow kind="products" />} />
        <Route path="/enroll/:courseId" element={<RedirectToEnrollmentFlow kind="courses" />} />
        <Route path="/donation" element={<DonationForm />} />
        <Route path="/donation/success" element={<DonationConfirmation />} />

        {/* Auth routes that need the auth card styling */}
        <Route path="/login" element={
          <AuthContainer>
            <AuthCard
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Logo>
                <img src={logo} width="80" height="114" alt="Faith Insight Logo" />
                <h1>Faith Insight</h1>
                <p>Learning Portal</p>
              </Logo>
              <SimpleLogin />
            </AuthCard>
          </AuthContainer>
        } />
        {/* The old sign-up-first flow is gone: new students pick courses/products in the public
            catalog and their account is created when they pay. Old links land there. */}
        <Route path="/signup" element={<Navigate to="/catalog" replace />} />
        <Route path="/reset-password" element={
          <AuthContainer>
            <AuthCard
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Logo>
                <img src={logo} width="80" height="114" alt="Faith Insight Logo" />
                <h1>Faith Insight</h1>
                <p>Learning Portal</p>
              </Logo>
              <ResetPassword />
            </AuthCard>
          </AuthContainer>
        } />
        <Route path="/attendance/checkin" element={<Navigate to="/login?redirect=%2Fattendance%2Fcheckin" replace />} />
        <Route path="/attendance/checkin/:sessionId" element={<AttendanceCheckIn />} />
        <Route path="/extensions/action" element={<ExtensionActionLanding />} />
        <Route path="/survey/respond/:token" element={<PublicSurveyPlayer />} />
        <Route path="/survey/class/:token" element={<SurveyClassLink />} />
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<RedirectToLoginWithReturn />} />
      </Routes>
    </Suspense>
  );
}

export default PublicRoutes;
